import { useState } from 'react';
import Button from '@/components/Button';
import CountrySelectBox, { CountryItem } from '@/components/CountrySelectBox';
import CurrencySelectBox, { CurrencyItem } from '@/components/CurrencySelectBox';
import TimezoneSelectBox, { TimezoneItem } from '@/components/TimezoneSelectBox';
import ToggleSwitch from '@/components/ToggleSwitch';
import { countryCodes } from '@/config';
import { Organization, OrganizationCreate } from '@/models/Organization';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useForm } from 'react-hook-form';

interface OrganizationFormProps {
  organization?: Organization;
  onSubmit: (organization: OrganizationCreate | Organization) => Promise<void>;
}

const OrganizationForm = ({ organization, onSubmit }: OrganizationFormProps) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<OrganizationCreate | Organization>({
    defaultValues: organization ?? {
      name: '',
      countryCode: '',
      timezone: '',
      status: 'ACTIVE',
      currencyCode: '',
    },
  });

  const countryCode = watch('countryCode');
  const status = watch('status');
  const timezone = watch('timezone');
  const currencyCode = watch('currencyCode');

  const handleStatusChange = (checked: boolean) => {
    setValue('status', checked ? 'ACTIVE' : 'INACTIVE');
  };

  const handleCountryOnSelect = (country: CountryItem | null) => {
    if (country) {
      setValue('countryCode', country.id);
      setValue('timezone', '');
    }
  };

  const handleTimezoneOnSelect = (timezone: TimezoneItem | null) => {
    if (timezone) {
      setValue('timezone', timezone.name);
    }
  };

  const handleCurrencyOnSelect = (currency: CurrencyItem | null) => {
    if (currency?.id) {
      setValue('currencyCode', currency.id);
    }
  };

  const onSubmitForm = async (data: OrganizationCreate | Organization) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div className="grid grid-cols-2 gap-4 lg:gap-8">
        <div className="col-span-2">
          <label htmlFor="name" className="block font-medium text-gray-700">
            Name:
          </label>
          <input
            id="name"
            {...register('name', { required: 'Name is required' })}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          {errors.name && <span className="text-red-500">{errors.name.message}</span>}
        </div>

        <div>
          <CountrySelectBox
            value={countryCode}
            countryCodes={countryCodes}
            onSelect={handleCountryOnSelect}
            requireSelection
          />
        </div>

        <div>
          <TimezoneSelectBox
            value={timezone}
            countryCode={countryCode}
            onSelect={handleTimezoneOnSelect}
            requireSelection
            disabled={!countryCode}
          />
        </div>

        <div>
          <CurrencySelectBox
            value={currencyCode}
            onSelect={handleCurrencyOnSelect}
            requireSelection
          />
        </div>

        <div className="col-span-2">
          <ToggleSwitch
            checked={status === 'ACTIVE'}
            onChange={handleStatusChange}
            labelText="Status:"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          variant="primary"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loadingIconClassName="h-5 w-5 mr-1"
          loading={loading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default OrganizationForm;
